import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Package, AlertTriangle, TrendingDown, BarChart3,
  ChevronRight, RefreshCw, BookOpen, Plus
} from 'lucide-react';
import { useItems, useStockSummary, useMovements } from '../hooks/useInventory';
import { useQueryClient } from '@tanstack/react-query';

// ── helpers ────────────────────────────────────────────────────────────────────
const fmtNum = (n: number) =>
  new Intl.NumberFormat('es-CO', { maximumFractionDigits: 2 }).format(n);

const ALERT_CATEGORIES = [
  { label: 'Helados base', emoji: '🍦', cat: 'helados-base' },
  { label: 'Frutas', emoji: '🍓', cat: 'frutas' },
  { label: 'Lácteos', emoji: '🥛', cat: 'lácteos' },
  { label: 'Empaques', emoji: '📦', cat: 'empaques' },
  { label: 'Comida rápida', emoji: '🍔', cat: 'comida-rapida' },
];

// ── sub-components ─────────────────────────────────────────────────────────────
function StatCard({
  icon, label, value, sub, color = 'blue',
}: {
  icon: React.ReactNode; label: string; value: string | number; sub?: string; color?: string;
}) {
  const colors: Record<string, string> = {
    blue: 'from-blue-500 to-blue-700',
    red: 'from-red-500 to-red-700',
    emerald: 'from-emerald-500 to-emerald-700',
    amber: 'from-amber-400 to-amber-600',
    violet: 'from-violet-500 to-violet-700',
  };
  return (
    <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${colors[color]} p-5 text-white shadow-lg`}>
      <div className="absolute -right-4 -top-4 opacity-20">{icon}</div>
      <div className="relative">
        <p className="text-xs font-semibold uppercase tracking-widest opacity-80">{label}</p>
        <p className="mt-1 text-3xl font-bold">{value}</p>
        {sub && <p className="mt-1 text-xs opacity-70">{sub}</p>}
      </div>
    </div>
  );
}

function StockBadge({ qty, minStock }: { qty: number; minStock?: number }) {
  const min = minStock ?? 0;
  if (qty <= 0) return (
    <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700">
      <AlertTriangle size={10} /> Agotado
    </span>
  );
  if (min > 0 && qty <= min) return (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">
      <TrendingDown size={10} /> Bajo
    </span>
  );
  return (
    <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
      OK
    </span>
  );
}

function RecentMovementRow({ mov }: { mov: any }) {
  const typeColors: Record<string, string> = {
    IN: 'bg-emerald-100 text-emerald-700',
    OUT: 'bg-red-100 text-red-700',
    ADJUST: 'bg-blue-100 text-blue-700',
    TRANSFER: 'bg-violet-100 text-violet-700',
  };
  const typeLabels: Record<string, string> = {
    IN: 'Entrada', OUT: 'Salida', ADJUST: 'Ajuste', TRANSFER: 'Traslado',
  };
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-[color:var(--color-border)]/40 last:border-0">
      <div className="flex items-center gap-2">
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${typeColors[mov.type] ?? 'bg-muted text-muted'}`}>
          {typeLabels[mov.type] ?? mov.type}
        </span>
        <span className="text-sm text-[color:var(--color-text)] line-clamp-1">{mov.reason ?? 'Sin motivo'}</span>
      </div>
      <span className={`text-sm font-bold ${mov.type === 'OUT' ? 'text-red-600' : 'text-emerald-600'}`}>
        {mov.type === 'OUT' ? '-' : '+'}{fmtNum(mov.qty)}
      </span>
    </div>
  );
}

// ── main page ──────────────────────────────────────────────────────────────────
const InventoryDashboard: React.FC = () => {
  const qc = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const { data: itemsPage, isLoading: loadingItems } = useItems({ limit: 100 });
  const { data: summary } = useStockSummary();
  const { data: movementsPage } = useMovements({ limit: 8 });

  const items = itemsPage?.data ?? [];
  const movements = (movementsPage as any)?.items ?? [];

  // KPI derivations
  const totalItems = itemsPage?.total ?? 0;
  const outOfStock = items.filter((i: any) => {
    const stock = (summary as any[])?.find((s: any) => s._id?.toString() === i.id)?.total ?? 0;
    return stock <= 0;
  }).length;
  const lowStock = items.filter((i: any) => {
    const stock = (summary as any[])?.find((s: any) => s._id?.toString() === i.id)?.total ?? 0;
    return i.minStock && stock > 0 && stock <= i.minStock;
  }).length;

  const handleRefresh = async () => {
    setRefreshing(true);
    await qc.invalidateQueries({ queryKey: ['inventory'] });
    setTimeout(() => setRefreshing(false), 800);
  };

  return (
    <div className="min-h-screen bg-[color:var(--color-bg)] px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-6xl space-y-8">

        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-muted">Módulo</p>
            <h1 className="text-3xl font-bold text-[color:var(--color-text)]">Inventario</h1>
            <p className="mt-1 text-sm text-muted">Control de insumos para heladería y comidas rápidas</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              className="inline-flex h-10 items-center gap-2 rounded-full border border-divider px-4 text-sm font-semibold transition hover:bg-surface-muted"
            >
              <RefreshCw size={15} className={refreshing ? 'animate-spin' : ''} />
              Actualizar
            </button>
            <Link
              to="/inventory/items"
              className="inline-flex h-10 items-center gap-2 rounded-full bg-primary px-4 text-sm font-bold text-white shadow hover:opacity-90"
            >
              <Plus size={15} /> Nuevo ítem
            </Link>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard
            icon={<Package size={72} />}
            label="Ítems totales"
            value={loadingItems ? '…' : totalItems}
            sub="productos registrados"
            color="blue"
          />
          <StatCard
            icon={<AlertTriangle size={72} />}
            label="Agotados"
            value={outOfStock}
            sub="requieren reposición urgente"
            color="red"
          />
          <StatCard
            icon={<TrendingDown size={72} />}
            label="Stock bajo"
            value={lowStock}
            sub="por debajo del mínimo"
            color="amber"
          />
          <StatCard
            icon={<BarChart3 size={72} />}
            label="Categorías"
            value={ALERT_CATEGORIES.length}
            sub="grupos de insumos"
            color="violet"
          />
        </div>

        {/* Alert: critical items */}
        {(outOfStock > 0 || lowStock > 0) && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={18} className="text-red-600" />
              <h2 className="font-bold text-red-800">Alerta de stock</h2>
            </div>
            <div className="space-y-2">
              {items
                .filter((i: any) => {
                  const stock = (summary as any[])?.find((s: any) => s._id?.toString() === i.id)?.total ?? 0;
                  return stock <= (i.minStock ?? 0);
                })
                .slice(0, 5)
                .map((item: any) => {
                  const stock = (summary as any[])?.find((s: any) => s._id?.toString() === item.id)?.total ?? 0;
                  return (
                    <div key={item.id} className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-red-900">{item.name}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-red-700">
                          {fmtNum(stock)} {item.unit} disponibles
                        </span>
                        <StockBadge qty={stock} minStock={item.minStock} />
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Stock summary table */}
          <div className="rounded-2xl border border-divider bg-surface p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-[color:var(--color-text)]">Estado del stock</h2>
              <Link to="/inventory/stock/summary" className="text-xs text-primary font-semibold hover:underline flex items-center gap-1">
                Ver todo <ChevronRight size={13} />
              </Link>
            </div>
            {loadingItems ? (
              <div className="space-y-3">{[...Array(5)].map((_, i) => (
                <div key={i} className="h-8 animate-pulse rounded-xl bg-surface-muted" />
              ))}</div>
            ) : (
              <div className="space-y-1">
                {items.slice(0, 10).map((item: any) => {
                  const stock = (summary as any[])?.find((s: any) => s._id?.toString() === item.id)?.total ?? 0;
                  const pct = item.minStock ? Math.min(100, (stock / (item.minStock * 3)) * 100) : 50;
                  const barColor = stock <= 0 ? 'bg-red-500' : stock <= (item.minStock ?? 0) ? 'bg-amber-400' : 'bg-emerald-500';
                  return (
                    <div key={item.id} className="group flex items-center gap-3 rounded-xl px-3 py-2 hover:bg-surface-muted">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="text-xs font-semibold text-[color:var(--color-text)] truncate">{item.name}</span>
                          <span className="text-xs text-muted shrink-0">{fmtNum(stock)} {item.unit}</span>
                        </div>
                        <div className="h-1.5 w-full rounded-full bg-surface-muted overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${barColor}`}
                            style={{ width: `${Math.max(3, pct)}%` }}
                          />
                        </div>
                      </div>
                      <StockBadge qty={stock} minStock={item.minStock} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Recent movements */}
          <div className="rounded-2xl border border-divider bg-surface p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-[color:var(--color-text)]">Movimientos recientes</h2>
              <Link to="/inventory/movements" className="text-xs text-primary font-semibold hover:underline flex items-center gap-1">
                Ver todo <ChevronRight size={13} />
              </Link>
            </div>
            {movements.length === 0 ? (
              <p className="text-sm text-muted text-center py-8">No hay movimientos registrados aún.</p>
            ) : (
              <div>
                {movements.map((mov: any) => (
                  <RecentMovementRow key={mov._id ?? mov.id} mov={mov} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Quick nav */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { to: '/inventory/items', icon: <Package size={20} />, label: 'Ítems & Categorías', desc: 'Crear y editar insumos' },
            { to: '/inventory/movements', icon: <TrendingDown size={20} />, label: 'Movimientos', desc: 'Entradas, salidas, ajustes' },
            { to: '/inventory/stock', icon: <BarChart3 size={20} />, label: 'Stock on-hand', desc: 'Stock en tiempo real' },
            { to: '/inventory/recipes', icon: <BookOpen size={20} />, label: 'Recetas', desc: 'Ingredientes por producto' },
          ].map(({ to, icon, label, desc }) => (
            <Link
              key={to}
              to={to}
              className="group flex flex-col gap-2 rounded-2xl border border-divider bg-surface p-4 shadow-sm transition hover:border-primary hover:shadow-md"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary transition group-hover:bg-primary group-hover:text-white">
                {icon}
              </div>
              <div>
                <p className="text-sm font-bold text-[color:var(--color-text)]">{label}</p>
                <p className="text-xs text-muted">{desc}</p>
              </div>
            </Link>
          ))}
        </div>

      </div>
    </div>
  );
};

export default InventoryDashboard;
